import { defineStore } from 'pinia'
import moment from 'moment'
import { flatten, camelCase } from 'lodash'
import repositoryFactory from '@/factories/repository-factory'
import SummaryRepository, { SummaryParams } from '@/repositories/summary-repository'
import Summary, { SummaryItemKey } from '@/models/Summary'
import { DefinitionInfoRainOrWaterItem, DefinitionInfoDefinitionSummary } from '@/models/initData/DefinitionInfo'
import { rangeToISO } from '@/lib/formatter'
import { useInitDataStore } from '@/stores/initDataStore'
import { WEATHER_FIELDS } from '@/lib/constants'

const storeKey = 'summary'

const formatDateTime = (datetime: string | undefined, format: string) => {
  if (!datetime) {
    return ''
  }

  if (moment(datetime).format('HHmm') === '0000') {
    return moment(datetime).add(-1, 'day').format(format).replaceAll('00:00', '24:00')
  } else {
    return moment(datetime).format(format)
  }
}

export const useSummaryStore = defineStore(storeKey, {
  state: () => ({
    data: [] as Summary[],
  }),

  getters: {
    find: state => (stationId: string) => {
      return state.data.find(s => s.stationId === stationId)
    },

    getRows:
      state =>
      (definitionInfo: DefinitionInfoRainOrWaterItem, info: DefinitionInfoDefinitionSummary, stationIds: string[]) => {
        const initDataStore = useInitDataStore()
        const key = camelCase(info.id) as SummaryItemKey

        return flatten(
          stationIds.map(stationId => {
            const item = definitionInfo.items.find(item => item.stationId === stationId)
            const datum = state.data.find(s => s.stationId === stationId)
            const station = initDataStore.findStation(stationId)

            if (!datum || !datum.items || !item) {
              return undefined
            }

            return item.summaryFieldMap[key].map(unit => {
              const lastKey = camelCase(unit)
              const tmpKey = lastKey === 'waterLevelAp' ? 'waterLevel' : lastKey
              const target = datum.items[tmpKey]

              if (info.formatList && info.formatList[tmpKey] && target) {
                const format = info.formatList[tmpKey]
                const value = formatDateTime(target[key] as string, format)

                return { value }
              }

              if (target === undefined) {
                return { value: undefined }
              }

              let value = target[key] as number

              if (lastKey === 'waterLevelAp') {
                value += station?.ap || 0
              }

              return { value, key: lastKey }
            })
          })
        )
      },

    getWeatherRows: state => (stationId: string, resolution: string) => {
      const rowKeys = ['avg', 'max', 'maxTime', 'min', 'minTime']
      const formats = {
        day: 'HH:mm',
        month: 'MM/DD HH:mm',
        year: 'MM/DD HH:mm',
      }
      const format = formats[resolution as keyof typeof formats]
      const rowNames = ['平均', '最高', '起時', '最低', '起時']
      const data = state.data.find(d => d.stationId === stationId)
      const items = data?.items
      const fieldIds = WEATHER_FIELDS.map(f => f.value)

      return rowKeys.map((key, index) => {
        const values = fieldIds.map(f => {
          if (items === undefined || items[f] === undefined) {
            return ''
          }

          const result = items[f][key as SummaryItemKey]

          if (result === undefined || result === null || result === '') {
            return ''
          }

          if (['moveWdMin10', 'moveMaxWdMin10'].includes(f)) {
            return ''
          }

          return ['maxTime', 'minTime'].includes(key) ? moment(result).format(format) : (result as number).toFixed(1)
        })

        return { title: rowNames[index], values }
      })
    },

    getWdCountRate: state => (stationId: string) => {
      const data = state.data.find(d => d.stationId === stationId)

      return Object.values(data?.items.moveWdMin10CountRate?.last || {})
    },
  },

  actions: {
    async fetch(params: SummaryParams) {
      const repository = (await repositoryFactory.create()).get(storeKey) as SummaryRepository

      return repository.get(params)
    },

    async fetchAll(stationIds: string[], type: string, range: Range, target: string[]) {
      const promises = [] as Promise<unknown>[]
      const results = [] as Summary[]

      stationIds.forEach(stationId => {
        const params = {
          stationId,
          range: {
            ...rangeToISO(range),
            type,
          },
          target,
        }

        const promise = this.fetch(params).then(summary => {
          results.push({ ...summary, stationId })
        })

        promises.push(promise)
      })

      await Promise.all(promises)

      this.data = results
    },

    async fetchWindCount(stationId: string, range: Range, type: string) {
      const params = {
        stationId,
        range: {
          ...range,
          type,
        },
        target: ['move_wd_min_10_count_rate'],
      }
      const repository = (await repositoryFactory.create()).get(storeKey) as SummaryRepository

      this.data = [await repository.get(params)]
    },
  },
})
